/**
 * File: clientNumberingService.ts
 * Path: apps/admin/src/shared/services/clientNumberingService.ts
 * Description: Client numbers (C001, C002...) and each client's contract
 * numbers (IDK-C012-01, -02...), handed out inside Firestore transactions so
 * two people saving at once can never get the same one.
 *
 * counters/clients holds the last client number given ({ last,
 * lastClientId }). It is created by "Number existing clients" (see
 * clientsRepo.numberExisting); until then new clients wait unnumbered, so
 * the clients from before numbering are numbered first, in the order they
 * were added. Each client document keeps its own contract count
 * ({ contractCount, lastContractId }).
 *
 * The rules (firestore.rules, CLIENT AND CONTRACT NUMBERS) allow only +1
 * steps, only with the document that takes the number written in the same
 * transaction, and never a change to a number once given. The format of the
 * printed reference lives in @idk/config references.ts.
 */

import {
  BUSINESSES_COLLECTION,
  CLIENT_COUNTER,
  FIRESTORE_COLLECTIONS,
  formatContractReference,
  toClient,
  type Client,
} from "@idk/config";
import { businessToClient, isClientSideStage, type Row } from "../utils/businessRecordUtils";
import {
  doc,
  serverTimestamp,
  type DocumentReference,
  type Firestore,
  type Transaction,
} from "firebase/firestore";

export const clientCounterRef = (db: Firestore): DocumentReference =>
  doc(db, CLIENT_COUNTER.collection, CLIENT_COUNTER.doc);

/**
 * The last client number given, or null before numbering has started.
 * Read inside the transaction, before any write (Firestore's rule).
 */
export const readClientCounter = async (
  tx: Transaction,
  db: Firestore,
): Promise<number | null> => {
  const snap = await tx.get(clientCounterRef(db));
  if (!snap.exists()) return null;
  const last: unknown = snap.data().last;
  return typeof last === "number" && Number.isSafeInteger(last) && last >= 0
    ? last
    : 0;
};

/**
 * Takes the next client number for `clientId`: moves the counter on by one
 * and returns the number, which the caller writes on that client in the
 * same transaction.
 */
export const claimClientNumber = (
  tx: Transaction,
  db: Firestore,
  last: number,
  clientId: string,
): number => {
  const next = last + 1;
  tx.update(clientCounterRef(db), { last: next, lastClientId: clientId });
  return next;
};

/** A client together with the collection that holds it (its business, or the old client). */
export type ClientInTx = Client & { home: string };

/**
 * The client, read inside the transaction; null when there is none. A
 * business counts only once it is won or past: before that it is a lead.
 */
export const readClientInTx = async (
  tx: Transaction,
  db: Firestore,
  clientId: string,
): Promise<ClientInTx | null> => {
  const business = await tx.get(doc(db, BUSINESSES_COLLECTION, clientId));
  if (business.exists()) {
    const row: Row = { id: business.id, ...business.data() };
    return isClientSideStage(row.stage) ? { ...businessToClient(row), home: BUSINESSES_COLLECTION } : null;
  }
  const snap = await tx.get(doc(db, FIRESTORE_COLLECTIONS.clients, clientId));
  const data = snap.data();
  return data ? { ...toClient(snap.id, data), home: FIRESTORE_COLLECTIONS.clients } : null;
};

/** What a contract stores once it has its place among the client's contracts. */
export interface ContractNumbering {
  reference: string;
  clientId: string;
  clientNumber: number;
  contractNumber: number;
}

/**
 * Gives `contractId` the client's next contract number and returns its
 * reference. The caller writes these fields on the contract in the same
 * transaction. Null when the client has no number yet.
 */
export const claimContractNumber = (
  tx: Transaction,
  db: Firestore,
  client: Client & { home?: string },
  contractId: string,
): ContractNumbering | null => {
  if (client.clientNumber === null) return null;
  const contractNumber = client.contractCount + 1;
  tx.update(doc(db, client.home ?? FIRESTORE_COLLECTIONS.clients, client.id), {
    contractCount: contractNumber,
    lastContractId: contractId,
    updatedAt: serverTimestamp(),
  });
  return {
    reference: formatContractReference(client.clientNumber, contractNumber),
    clientId: client.id,
    clientNumber: client.clientNumber,
    contractNumber,
  };
};
